import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { API_CONFIG, ApiConfig } from '../../api-config';

export interface CompaniesSelectOption {
  value: string;
  label: string;
}

/**
 * Catálogo de empresas filtrado por clase.
 * GET {apiUrl}/selects/companies?type={code}
 * → { success, data: [{ id, text }] }
 */
@Injectable({ providedIn: 'root' })
export class CompaniesSelectService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);
  private readonly cache = new Map<string, CompaniesSelectOption[]>();

  getOptions(type: string): Observable<CompaniesSelectOption[]> {
    const code = type.trim();
    if (!code) return of([]);

    const cacheKey = code.toUpperCase();
    const cached = this.cache.get(cacheKey);
    if (cached) return of(cached);

    return this.httpClient
      .get(`${this.apiConfig.apiUrl}/selects/companies`, {
        params: { type: code },
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        map((resp) => this.parseSelectResponse(resp.body)),
        tap((options) => this.cache.set(cacheKey, options)),
        catchError(() => of([])),
      );
  }

  private parseSelectResponse(body: unknown): CompaniesSelectOption[] {
    if (typeof body !== 'string' || !body) return [];
    try {
      const parsed = JSON.parse(body) as {
        success?: boolean;
        data?: Array<{ id: string; text?: string; label?: string; name?: string }>;
      };
      if (!parsed || parsed.success !== true || !Array.isArray(parsed.data)) {
        return [];
      }
      return parsed.data.map((item) => ({
        value: item.id || '',
        label: item.text || item.label || item.name || '',
      }));
    } catch {
      return [];
    }
  }
}
